import { colors } from 'app-v2/design-systems/theme/colors'
import { ButtonMedium, style } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { H4, SmallText } from 'app/common/typography'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { adyenPaymentMethods, featureCodes } from 'shared/utils'
import styled from 'styled-components'
import {
    isApplePayAvailable,
    isGooglePayAvailable
} from '../../screens/cart-summary/AdyenOneClickPaymentWrapper'
import { SPACING } from 'app-v2/design-tokens'
import { FeatureToggle } from 'app/common/feature-toggle'
import { string } from 'yup'

type ButtonFooterProps = {
    labelKey: string,
    labelParams?: any,
    color?: string,
    disabled?: boolean,
    actionUrl?: string,
    onClick?: any,
    params?: { [string]: any },
    shouldStick?: boolean,
    showTermsOfSales?: boolean,
    isAdyen?: boolean
}

export const ButtonFooter = ({
    labelKey,
    labelParams,
    color = colors.crunchyGreen,
    disabled = false,
    actionUrl,
    onClick,
    params,
    shouldStick,
    showTermsOfSales,
    isAdyen = false
}: ButtonFooterProps) => {
    const buttonColor = disabled ? colors.crunchyGreenDisabled : color
    const intl = useIntl()
    const enableApplePay = isAdyen && isApplePayAvailable
    const enableGooglePay = isAdyen && isGooglePayAvailable
    return (
        <Container shouldStick={shouldStick}>
            {showTermsOfSales && (
                <StyledTermsOfSalesContainer>
                    <SmallText>
                        {intl.formatMessage({
                            id: 'clickandcollect.userinfos.cgv.start'
                        })}

                        <StyledTextLink
                            href={intl.formatMessage({
                                id: 'routes.cgv.full'
                            })}
                            target="_blank"
                            rel="noopener noreferrer">
                            {intl.formatMessage({
                                id: 'clickandcollect.userinfos.cgv.link'
                            })}
                        </StyledTextLink>
                    </SmallText>
                </StyledTermsOfSalesContainer>
            )}
            {enableGooglePay && (
                <FeatureToggle feature={featureCodes.ADYEN_GOOGLE_PAY_WEB}>
                    <StyledOneClickPaymentButton
                        id={`${adyenPaymentMethods.GOOGLE_PAY}-container`}
                    />
                </FeatureToggle>
            )}
            {enableApplePay && (
                <FeatureToggle feature={featureCodes.ADYEN_APPLE_PAY_WEB}>
                    <StyledOneClickPaymentButton
                        id={`${adyenPaymentMethods.APPLE_PAY}-container`}
                    />
                </FeatureToggle>
            )}
            <FormattedMessage id={labelKey} values={labelParams}>
                {(text: string) =>
                    !!actionUrl ? (
                        <StyledLink
                            url={actionUrl}
                            onClick={onClick}
                            disabled={disabled}
                            background={buttonColor}
                            params={params}>
                            <H4 center>{text}</H4>
                        </StyledLink>
                    ) : (
                        <StyledButtonMedium
                            onClick={onClick}
                            disabled={disabled}
                            background={buttonColor}>
                            <H4 center>{text}</H4>
                        </StyledButtonMedium>
                    )
                }
            </FormattedMessage>
        </Container>
    )
}

const StyledOneClickPaymentButton = styled.div`
    padding-bottom: ${SPACING.XXS};
    & button {
        width: calc(100vw - ${SPACING.XXL});
        max-width: 450px;
        border-radius: 10px;
        height: 41px !important;
    }
`

const Container = styled.div`
    width: 100%;
    padding: ${SPACING.S} 0;
    display: flex;
    flex-direction: column;
    z-index: 1000;
    justify-content: center;
    align-items: center;
    ${props =>
        props.shouldStick &&
        `position: fixed;
        bottom: 0;
        right: 0;
        left: 0;
        background-color: white;
        border-top: solid 1px ${colors.lightGrey}`}
`

const StyledTextLink = styled.a`
    text-decoration: underline;
`

const StyledLink = styled(Link)`
    ${style.buttonMedium};
    background-color: ${props => props.background};
    border: none;
    pointer-events: ${props => props.disabled && 'none'};
    padding-left: unset;
    padding-right: unset;
    width: calc(100% - ${SPACING.XXL});
    max-width: 450px;
`

const StyledButtonMedium = styled(ButtonMedium)`
    background-color: ${props => props.background};
    border: none;
    width: calc(100% - ${SPACING.XXL});
    max-width: 450px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
`

const StyledTermsOfSalesContainer = styled.div`
    padding: ${SPACING.XXXS} ${SPACING.M} ${SPACING.S} ${SPACING.M};
`
